import React, { useState, Fragment, useEffect } from "react";
import { clone, lensPath, path, prop, set, sortBy } from "ramda";
import { withStyles } from "@material-ui/core/styles";
import {
  hasError,
  validateContributor,
  validateContributorField,
} from "creator-application/content/contentValidation";
import { firstError } from "util/validation";
import { isBlank, notBlank } from "util/ext";
import { useContributorRoles } from "../../vocabularyServices";

import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import Grid from "@material-ui/core/Grid";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { InputLabel, MenuItem } from "@material-ui/core";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import { Skeleton } from "@material-ui/lab";

const styles = theme => ({
  root: {
    margin: 0,
    padding: "25px 0 10px 25px",
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.primary.darker,
    fontWeight: "bolder",
  },
});

const DialogTitle = withStyles(styles)(props => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h5">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon fontSize="large" />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const ContributorDialog = ({
  index,
  contributor,
  onCancel,
  onSave,
  title,
  visible,
}) => {
  const roles = useContributorRoles();
  const [errors, setErrors] = useState({});
  const [internal, setInternal] = useState({});
  useEffect(() => {
    setInternal(clone(contributor));
  }, [contributor]);

  const onBlur = (field, value) => () => {
    console.log("On Blur");
    setErrors(
      validateContributorField(internal.contributorType, field, value, errors),
    );
  };

  const onFieldChange = path => (event, optVal) => {
    console.log("Field Change");
    const value = optVal || event.target.value;
    setInternal(prev => set(lensPath(path), value, prev));
  };

  const onTypeChange = path => event => {
    console.log("Type Change");
    const value = event.target.value;
    console.log(value);
    setInternal(prev => set(lensPath(path), value, prev));
  };

  const handleSave = () => {
    const errors = validateContributor(internal.contributorType, internal);
    if (notBlank(errors)) {
      setErrors(errors);
    } else {
      onSave(internal, index);
    }
  };

  return (
    <Dialog open={visible} onClose={onCancel}>
      <DialogTitle id="form-dialog-title" onClose={onCancel}>
        {title}
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12}>
            <FormControl fullWidth>
              <InputLabel htmlFor="contributor-type">Type</InputLabel>
              <Select
                value={path(["contributorType"], internal) || "PERSON"}
                onChange={onTypeChange(["contributorType"])}
                inputProps={{
                  name: "contributor-type",
                  id: "contributor-type",
                }}
              >
                <MenuItem value="PERSON" key="PERSON">
                  Person
                </MenuItem>
                <MenuItem value="CORPORATION" key="CORPORATION">
                  Corporation
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={12}>
            {roles.isLoading ? (
              <Skeleton variant="text" />
            ) : (
              <Autocomplete
                disableClearable
                autoSelect
                options={sortBy(prop("id"), roles.data)}
                value={path(["role"], internal) || ""}
                getOptionLabel={role =>
                  notBlank(role) ? role.displayName : ""
                }
                getOptionSelected={(role, val) =>
                  isBlank(val) || role.id === val.id
                }
                onChange={onFieldChange(["role"])}
                renderInput={params => {
                  params.inputProps["aria-autocomplete"] = "none";
                  params.inputProps["id"] = null;
                  return (
                    <TextField
                      {...params}
                      type="text"
                      onBlur={onBlur("role", internal.role)}
                      error={hasError(errors, "role")}
                      helperText={firstError(errors, "role")}
                      name={"role-" + Math.random()}
                      variant="standard"
                      label="Role"
                      fullWidth
                    />
                  );
                }}
              />
            )}
          </Grid>

          {internal.contributorType === "PERSON" ? (
            <Fragment>
              <Grid item xs={6} sm={4}>
                <TextField
                  label="Prefix (optional)"
                  value={internal.prefix || ""}
                  onChange={onFieldChange(["prefix"])}
                  error={hasError(errors, "prefix")}
                  helperText={firstError(errors, "prefix")}
                  onBlur={onBlur("prefix", internal.prefix)}
                  inputProps={{ maxLength: 50 }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="First"
                      value={internal.firstName || ""}
                      onChange={onFieldChange(["firstName"])}
                      error={hasError(errors, "firstName")}
                      helperText={firstError(errors, "firstName")}
                      onBlur={onBlur("firstName", internal.firstName)}
                      inputProps={{ maxLength: 100 }}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Middle"
                      value={internal.middleName || ""}
                      onChange={onFieldChange(["middleName"])}
                      error={hasError(errors, "middleName")}
                      helperText={firstError(errors, "middleName")}
                      onBlur={onBlur("middleName", internal.middleName)}
                      inputProps={{ maxLength: 100 }}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Last"
                      value={internal.lastName || ""}
                      onChange={onFieldChange(["lastName"])}
                      error={hasError(errors, "lastName")}
                      helperText={firstError(errors, "lastName")}
                      onBlur={onBlur("lastName", internal.lastName)}
                      inputProps={{ maxLength: 100 }}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={6} sm={4}>
                <TextField
                  label="Suffix (optional)"
                  value={internal.suffix || ""}
                  onChange={onFieldChange(["suffix"])}
                  error={hasError(errors, "suffix")}
                  helperText={firstError(errors, "suffix")}
                  onBlur={onBlur("suffix", internal.suffix)}
                  inputProps={{ maxLength: 100 }}
                  fullWidth
                />
              </Grid>
            </Fragment>
          ) : (
            <Fragment>
              <Grid item xs={12} sm={12}>
                <TextField
                  label="Corporation Name"
                  value={internal.fullName || ""}
                  onChange={onFieldChange(["fullName"])}
                  error={hasError(errors, "fullName")}
                  helperText={firstError(errors, "fullName")}
                  onBlur={onBlur("fullName", internal.fullName)}
                  inputProps={{ maxLength: 100 }}
                  fullWidth
                />
              </Grid>
            </Fragment>
          )}
          <Grid item xs={12} sm={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Website (optional)"
                  value={internal.website || ""}
                  onChange={onFieldChange(["website"])}
                  error={hasError(errors, "website")}
                  helperText={firstError(errors, "website")}
                  onBlur={onBlur("website", internal.website)}
                  inputProps={{ maxLength: 2048 }}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="X handle (optional)"
                  value={internal.twitterHandle || ""}
                  onChange={onFieldChange(["twitterHandle"])}
                  error={hasError(errors, "twitterHandle")}
                  helperText={firstError(errors, "twitterHandle")}
                  onBlur={onBlur("twitterHandle", internal.twitterHandle)}
                  inputProps={{ maxLength: 30 }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" color="primary" onClick={handleSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContributorDialog;
